import React, { FC, useMemo, useRef, useState } from 'react'

import clsx from 'clsx'
import { useClickAway } from 'react-use'
import styled, { css } from 'styled-components'

import { SearchedUserListItem } from 'src/components/molecules'

import { useGetExternalUsers, useGetUsers, useUser } from 'src/hooks'
import { hasElementInId } from 'src/libs/helper'
import { ProfileEntity, UserEntity } from 'src/types'

const Container = styled.div<{ isFocus: boolean }>`
  width: 100%;
  min-height: 3.57rem;
  box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.05);
  border-radius: 0.29rem;
  max-height: 37.5rem;
  padding: 0.86rem;
  transition: all 0.1s ease-in-out;
  position: relative;
  display: flex;
  flex-wrap: wrap;

  &:hover:not(:focus):not(:disabled) {
    box-shadow: inset 0 0 8px rgba(0, 0, 0, 0.1);
  }

  ${props =>
    props.isFocus &&
    css`
      border-radius: 0.29rem 0.29rem 0 0;
    `}
`

const ContentInput = styled.input`
  outline: none;
  resize: none;
  height: 2em;
  line-height: 2em;
  width: 100%;
`

const SearchedContainer = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  transform: translateY(100%);
  z-index: 100;
  filter: drop-shadow(0px 5px 20px rgba(122, 137, 160, 0.1));
  width: 100%;
`

const ProjectList = styled.ul`
  border-radius: 0 0 8px 8px;
  overflow: auto;
  max-height: 21.43rem;
`

type Props = {
  alreadyAssignedUserIds: number[]
  profile: ProfileEntity
  onSelectUser: (user: UserEntity) => void
  className?: string
}

export const SearchAssignUserInput: FC<Props> = ({
  alreadyAssignedUserIds,
  profile,
  onSelectUser,
  className,
}) => {
  const SearchInputId = 'user-search-input'

  const [inputText, setInputText] = useState<string>('')
  const [isFocus, setIsFocus] = useState(false)
  const [showSearchUsers, setShowSearchUsers] = useState(false)

  // 空白文字とカンマで分割して、空文字を除外
  const searchWords = useMemo(() => inputText.split(/[\s,]/).filter(Boolean), [inputText])

  const { users: internalUsers } = useGetUsers()
  const { externalUsers } = useGetExternalUsers()
  const { searchUsers } = useUser()

  const targetUsers: UserEntity[] = useMemo(() => {
    const users = [...internalUsers, ...externalUsers].filter(user =>
      alreadyAssignedUserIds.every(assignedProjectId => assignedProjectId !== user.id)
    )
    return inputText ? searchUsers({ users, searchWord: inputText }) : users
  }, [alreadyAssignedUserIds, externalUsers, inputText, internalUsers, searchUsers])

  const handleOnSelectTargetUser = (user: UserEntity) => {
    onSelectUser(user)
  }

  const handleOnFocusInput = () => {
    setIsFocus(true)
    setShowSearchUsers(true)
  }

  const ref = useRef(null)
  // 検索結果の外をクリックした時
  useClickAway(ref, event => {
    // クリックした要素が検索を行うためのinputだった場合は何もしない
    const target = event.target as HTMLElement
    if (target && hasElementInId(target, SearchInputId)) {
      return
    }

    if (showSearchUsers) {
      setShowSearchUsers(false)
      setIsFocus(false)
    }
  })

  return (
    <div className={className}>
      <div className="tw-relative">
        <Container
          isFocus={isFocus}
          className={clsx(
            'tw-border tw-border-border-secondary tw-bg-surface-primary',
            isFocus && 'tw-border tw-border-roovPrimary-default'
          )}
        >
          <ContentInput
            id={SearchInputId}
            placeholder="お名前・メールアドレス・カスタムロール・キーワードで検索（複数可）"
            value={inputText}
            onChange={event => setInputText(event.target.value)}
            onFocus={handleOnFocusInput}
            className="tw-caret-correctGreen-default"
          />
        </Container>

        {showSearchUsers && (
          <SearchedContainer ref={ref}>
            <ProjectList className="tw-border tw-border-border-secondary">
              {targetUsers.map(user => (
                <li
                  key={user.id}
                  role="presentation"
                  className="tw-border-b tw-border-border-secondary last:tw-border-0"
                >
                  <SearchedUserListItem
                    user={user}
                    profile={profile}
                    searchWords={searchWords}
                    onSelect={() => handleOnSelectTargetUser(user)}
                  />
                </li>
              ))}
            </ProjectList>
          </SearchedContainer>
        )}
      </div>
    </div>
  )
}
